import React, { useState, useEffect, useCallback } from 'react';
import useWishlist from '../hooks/useWishlist';

const decodeHtml = (value) => {
    if (typeof value !== 'string') return value;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
};

const WishlistPage = () => {
    const { wishlistItems, loading, isInWishlist, toggleWishlistItem, isToggling, refreshWishlist } = useWishlist();
    const [products, setProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [addingToCart, setAddingToCart] = useState({});
    const [selectedItems, setSelectedItems] = useState({});

    // Fetch product details when wishlist items change
    useEffect(() => {
        if (loading) return;
        
        if (wishlistItems.length === 0) {
            setProducts([]);
            setLoadingProducts(false);
            return;
        }

        fetchProducts(wishlistItems);
    }, [wishlistItems, loading]);

    useEffect(() => {
        // Keep selections in sync with current wishlist
        setSelectedItems(prev => {
            const next = {};
            wishlistItems.forEach(id => {
                const key = parseInt(id, 10);
                if (prev[key]) {
                    next[key] = true;
                }
            });
            return next;
        });
    }, [wishlistItems]);

    const fetchProducts = async (productIds) => {
        setLoadingProducts(true);
        try {
            // Fetch products from WooCommerce REST API
            const response = await fetch(`/wp-json/wc/store/v1/products?include=${productIds.join(',')}&per_page=${productIds.length}`);
            if (!response.ok) throw new Error('Failed to fetch products');
            
            const data = await response.json();
            setProducts(data);
        } catch (error) {
            console.error('Error fetching products:', error);
            setProducts([]);
        } finally {
            setLoadingProducts(false);
        }
    };

    const handleAddToCart = async (product) => {
        if (addingToCart[product.id]) return;
        
        setAddingToCart(prev => ({ ...prev, [product.id]: true }));
        
        try {
            const response = await fetch(`/?wc-ajax=add_to_cart`, {
                method: 'POST',
                body: new URLSearchParams({
                    product_id: product.id,
                    quantity: 1
                }),
                credentials: 'same-origin'
            });
            
            const data = await response.json();
            
            if (data.error) {
                alert('Σφάλμα: ' + (data.error || 'Δεν ήταν δυνατή η προσθήκη στο καλάθι'));
            } else {
                // Update cart fragments if available
                if (data.fragments) {
                    Object.keys(data.fragments).forEach(key => {
                        const elements = document.querySelectorAll(key);
                        elements.forEach(el => {
                            el.outerHTML = data.fragments[key];
                        });
                    });
                }
                
                // Update cart count
                if (data.cart_hash) {
                    const cartCounts = document.querySelectorAll('.cart-icon-num');
                    cartCounts.forEach(el => {
                        const currentCount = parseInt(el.textContent) || 0;
                        el.textContent = currentCount + 1;
                    });
                }
                
                // Show success feedback
                const btn = document.querySelector(`[data-product-id="${product.id}"] .df-wishlist__add-to-cart`);
                if (btn) {
                    const originalText = btn.textContent;
                    btn.textContent = '✓ Προστέθηκε!';
                    btn.classList.add('df-wishlist__add-to-cart--success');
                    setTimeout(() => {
                        btn.textContent = originalText;
                        btn.classList.remove('df-wishlist__add-to-cart--success');
                    }, 2000);
                }
            }
        } catch (error) {
            console.error('Error adding to cart:', error);
            alert('Σφάλμα κατά την προσθήκη στο καλάθι');
        } finally {
            setAddingToCart(prev => ({ ...prev, [product.id]: false }));
        }
    };

    const handleRemove = async (productId) => {
        await toggleWishlistItem(productId);
    };

    const toggleSelect = (productId) => {
        const id = parseInt(productId, 10);
        setSelectedItems(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const selectedIds = Object.keys(selectedItems).filter(id => selectedItems[id]).map(id => parseInt(id, 10));
    const hasSelected = selectedIds.length > 0;

    const handleRemoveSelected = async () => {
        for (const id of selectedIds) {
            await toggleWishlistItem(id);
        }
    };

    const handleAddSelectedToCart = async () => {
        const selectedProducts = products.filter(p => selectedItems[p.id]);
        for (const product of selectedProducts) {
            if (product.is_in_stock === false) continue;
            await handleAddToCart(product);
        }
    };

    const handleClearWishlist = async () => {
        try {
            const formData = new FormData();
            formData.append('action', 'df_clear_wishlist');

            const response = await fetch('/wp-admin/admin-ajax.php', {
                method: 'POST',
                body: formData,
                credentials: 'same-origin'
            });

            const data = await response.json();
            if (data.success) {
                setSelectedItems({});
                await refreshWishlist();
            } else {
                alert('Σφάλμα: Δεν ήταν δυνατή η εκκαθάριση της λίστας');
            }
        } catch (error) {
            console.error('Error clearing wishlist:', error);
            alert('Σφάλμα κατά την εκκαθάριση της λίστας');
        }
    };

    // Empty state
    if (!loading && !loadingProducts && wishlistItems.length === 0) {
        return (
            <div className="df-wishlist__empty">
                <div className="df-wishlist__empty-icon">
                    <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#ddd" strokeWidth="1.5">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                    </svg>
                </div>
                <h2 className="df-wishlist__empty-title">Η λίστα επιθυμιών σου είναι άδεια</h2>
                <p className="df-wishlist__empty-text">Ξεκίνα να προσθέτεις προϊόντα που σου αρέσουν!</p>
                <a href="/device-finder/" className="df-wishlist__cta-button">
                    Βρες θήκη για τη συσκευή σου
                </a>
            </div>
        );
    }

    // Loading state
    if (loading || loadingProducts) {
        return (
            <div className="df-wishlist__loading">
                <div className="df-wishlist__spinner"></div>
                <p className="df-wishlist__loading-text">Φόρτωση λίστας επιθυμιών...</p>
            </div>
        );
    }

    return (
        <div className="df-wishlist">
            <div className="df-wishlist__header">
                <h1 className="df-wishlist__title">
                    <svg className="df-wishlist__title-icon" width="28" height="28" viewBox="0 0 24 24" fill="#e53935" stroke="none">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                    </svg>
                    Η Λίστα Επιθυμιών μου
                </h1>
                <div className="df-wishlist__header-actions">
                    <span className="df-wishlist__count">{products.length} {products.length === 1 ? 'προϊόν' : 'προϊόντα'}</span>
                    {products.length > 0 && (
                        <button
                            type="button"
                            className="df-wishlist__bulk-btn df-wishlist__bulk-btn--clear"
                            onClick={handleClearWishlist}
                        >
                            Εκκαθάριση λίστας
                        </button>
                    )}
                    {hasSelected && (
                        <div className="df-wishlist__bulk-actions">
                            <button
                                type="button"
                                className="df-wishlist__bulk-btn df-wishlist__bulk-btn--remove"
                                onClick={handleRemoveSelected}
                            >
                                Διαγραφή επιλεγμένων
                            </button>
                            <button
                                type="button"
                                className="df-wishlist__bulk-btn df-wishlist__bulk-btn--add"
                                onClick={handleAddSelectedToCart}
                            >
                                Προσθήκη στο καλάθι
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="df-wishlist__grid">
                {products.map(product => (
                    <ProductCard
                        key={product.id}
                        product={product}
                        onRemove={handleRemove}
                        onAddToCart={handleAddToCart}
                        isRemoving={isToggling(product.id)}
                        isAddingToCart={addingToCart[product.id]}
                        isSelected={!!selectedItems[product.id]}
                        onToggleSelect={toggleSelect}
                    />
                ))}
            </div>
        </div>
    );
};

const ProductCard = ({ product, onRemove, onAddToCart, isRemoving, isAddingToCart, isSelected, onToggleSelect }) => {
    const imageUrl = product.images?.[0]?.src || product.images?.[0]?.thumbnail || '/wp-content/uploads/woocommerce-placeholder.png';
    const isInStock = product.is_in_stock !== false;
    const productName = decodeHtml(product.name);
    const price = product.prices?.price ? (parseInt(product.prices.price) / 100).toFixed(2) : null;
    const regularPrice = product.prices?.regular_price ? (parseInt(product.prices.regular_price) / 100).toFixed(2) : null;
    const isOnSale = price && regularPrice && price !== regularPrice;

    const cardClasses = ['df-wishlist__card'];
    if (isRemoving) cardClasses.push('df-wishlist__card--removing');
    if (isSelected) cardClasses.push('df-wishlist__card--selected');

    return (
        <div 
            className={cardClasses.join(' ')}
            data-product-id={product.id}
        >
            {/* Select Checkbox */}
            <label className="df-wishlist__select">
                <input
                    type="checkbox"
                    className="df-wishlist__checkbox"
                    checked={isSelected}
                    onChange={() => onToggleSelect(product.id)}
                />
                <span className="df-wishlist__checkbox-ui" aria-hidden="true"></span>
            </label>
            {/* Remove Button */}
            <button
                className="df-wishlist__remove-btn"
                onClick={() => onRemove(product.id)}
                disabled={isRemoving}
                title="Αφαίρεση από τη Λίστα"
            >
                {isRemoving ? (
                    <span className="df-wishlist__mini-spinner"></span>
                ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                )}
            </button>

            {/* Sale Badge */}
            {isOnSale && (
                <span className="df-wishlist__sale-badge">Προσφορά</span>
            )}

            {/* Image */}
            <a href={product.permalink} className="df-wishlist__image-link">
                <div className="df-wishlist__image-container">
                    <img 
                        className="df-wishlist__image"
                        src={imageUrl} 
                        alt={productName}
                        loading="lazy"
                    />
                </div>
            </a>

            {/* Content */}
            <div className="df-wishlist__content">
                <a href={product.permalink} className="df-wishlist__title-link">
                    <h3 className="df-wishlist__product-title">{productName}</h3>
                </a>

                <div className="df-wishlist__price-container">
                    {isOnSale && (
                        <span className="df-wishlist__regular-price">{regularPrice}€</span>
                    )}
                    <span className="df-wishlist__price">{price ? `${price}€` : ''}</span>
                </div>

                {!isInStock ? (
                    <span className="df-wishlist__out-of-stock">Εκτός Αποθέματος</span>
                ) : (
                    <button
                        className="df-wishlist__add-to-cart"
                        onClick={() => onAddToCart(product)}
                        disabled={isAddingToCart}
                    >
                        {isAddingToCart ? 'Προσθήκη...' : 'Προσθήκη στο Καλάθι'}
                    </button>
                )}
            </div>
        </div>
    );
};

export default WishlistPage;
